import React, { useState, useEffect, useCallback } from 'react';
import DashboardCard from '../../../shared/ui/cards/DashboardCard';
import { StatusActionButton, StatsGrid, StatItem, HorizontalCategoryTabs } from '../../../shared/ui';
import { questionsV2Api, QuestionV2 } from '../../../api/questionsV2Api';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useDashboardCounters } from '../../../shared/contexts/DashboardCountersContext';
import CreateSupplyV2Modal from './CreateSupplyV2Modal';
import QuestionV2MessengerModal from '../../questionsV2/ui/QuestionV2MessengerModal';
import './SupplyCard.css';

interface SupplyCardProps {
  siteId?: number;
}

const SupplyCard: React.FC<SupplyCardProps> = ({ siteId }) => {
  const [isCollapsed, setIsCollapsed] = useState(true); // Ленивая загрузка: по умолчанию свернута
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false); // Отслеживаем открытие fullscreen
  const [supplies, setSupplies] = useState<QuestionV2[]>([]);
  const [statistics, setStatistics] = useState<{ totalSupplies: number; openSupplies: number; closedSupplies: number; userSupplies: number } | null>(null);
  const [loading, setLoading] = useState(false); // Начинаем с false, т.к. не загружаем сразу
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMessengerModal, setShowMessengerModal] = useState(false);
  const [selectedSupply, setSelectedSupply] = useState<QuestionV2 | null>(null);
  const [filter, setFilter] = useState<'all' | 'open' | 'closed' | 'my'>('all');
  const { user } = useAuth();
  const countersContext = useDashboardCounters();
  
  // Получаем счетчик из Context (если доступен) или null
  const supplyCount = countersContext?.counters?.activeSupply?.hasAccess
    ? (countersContext.counters.activeSupply.count ?? 0)
    : 0;
  const countersLoading = countersContext?.loading ?? false;

  // Загрузка данных
  const loadData = useCallback(async () => {
    if (!siteId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      
      const suppliesData = await questionsV2Api.getSiteContentByTypeV2(siteId, 3); // 3 = Supply
      
      setSupplies(suppliesData);
      
      // Вычисляем статистику
      const stats = {
        totalSupplies: suppliesData.length,
        openSupplies: suppliesData.filter((s: QuestionV2) => !s.closed).length,
        closedSupplies: suppliesData.filter((s: QuestionV2) => s.closed).length,
        userSupplies: suppliesData.filter((s: QuestionV2) => s.userId === user?.id).length
      };
      setStatistics(stats);
    } catch (err) {
      console.error('Error loading supplies:', err);
    } finally {
      setLoading(false);
    }
  }, [siteId, user?.id]);

  // Счетчики загружаются через Context в ObjectPage

  // Ленивая загрузка: загружаем данные когда карточка раскрыта (inline) или fullscreen модальное окно открыто
  useEffect(() => {
    if (!isCollapsed || isFullscreenOpen) {
      loadData();
    }
  }, [isCollapsed, isFullscreenOpen, loadData]);

  // Обработчики событий
  const handleCreateSupply = () => {
    setShowCreateModal(true);
  };

  const handleSupplyCreated = () => {
    setShowCreateModal(false);
    loadData();
  };

  const handleSupplyClick = (supply: QuestionV2) => {
    setSelectedSupply(supply);
    setShowMessengerModal(true);
  };

  const handleSupplyUpdated = () => {
    loadData();
  };

  // Фильтрация снабжения
  const getFilteredSupplies = () => {
    switch (filter) {
      case 'open':
        return supplies.filter(supply => !supply.closed);
      case 'closed':
        return supplies.filter(supply => supply.closed);
      case 'my':
        return supplies.filter(supply => supply.userId === user?.id);
      default:
        return supplies;
    }
  };

  // Создание элементов списка
  const createSupplyItems = (): QuestionV2[] => {
    return getFilteredSupplies();
  };

  const supplyItems = createSupplyItems();

  // Подготовка данных для StatsGrid
  const statsData: StatItem[] = [
    {
      id: 'total',
      value: statistics?.totalSupplies || 0,
      label: 'Всего',
      icon: 'ri-truck-line',
      color: 'muted'
    },
    {
      id: 'open',
      value: statistics?.openSupplies || 0,
      label: 'В работе',
      icon: 'ri-time-line',
      color: 'warning'
    },
    {
      id: 'closed',
      value: statistics?.closedSupplies || 0,
      label: 'Выполнено',
      icon: 'ri-check-line',
      color: 'success'
    },
    {
      id: 'my',
      value: statistics?.userSupplies || 0,
      label: 'Мои',
      icon: 'ri-user-line',
      color: 'primary'
    }
  ];

  return (
    <DashboardCard
      id="supply-card"
      title="Снабжение"
      icon="ri-shopping-bag-3-line"
      isCollapsible={true}
      isCollapsed={isCollapsed}
      onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      onFullscreenOpenChange={setIsFullscreenOpen}
      expandMode="fullscreen"
      compactTileVariant="supply"
      counters={supplyCount > 0 ? undefined : [0]}
      countersLoading={countersLoading}
      warningCounter={supplyCount > 0 ? supplyCount : undefined}
      warningCounterTitle="Активных заявок снабжения"
    >
      {!siteId ? (
        <div className="supply-placeholder">
          <i className="ri-truck-line"></i>
          <h3>Выберите проект</h3>
          <small>Для работы с заявками на снабжение необходимо выбрать конкретный проект</small>
        </div>
      ) : (
        <>
          {/* Кнопка создания заявки на снабжение */}
          <div style={{ padding: '0 0 20px 0' }}>
            <StatusActionButton
              variant="primary"
              fullWidth
              onClick={handleCreateSupply}
            >
              Создать заявку на снабжение
            </StatusActionButton>
          </div>

          {/* Статистика */}
          <StatsGrid items={statsData} />

          {/* Фильтры */}
          <HorizontalCategoryTabs
            categories={[
              `В работе (${statistics?.openSupplies || 0})`,
              `Выполнено (${statistics?.closedSupplies || 0})`,
              `Мои (${statistics?.userSupplies || 0})`
            ]}
            value={
              filter === 'all' 
                ? null 
                : filter === 'open' 
                  ? `В работе (${statistics?.openSupplies || 0})`
                  : filter === 'closed'
                    ? `Выполнено (${statistics?.closedSupplies || 0})`
                    : `Мои (${statistics?.userSupplies || 0})`
            }
            onChange={(category) => {
              if (category === null) {
                setFilter('all');
              } else if (category.startsWith('В работе')) {
                setFilter('open');
              } else if (category.startsWith('Выполнено')) {
                setFilter('closed');
              } else if (category.startsWith('Мои')) {
                setFilter('my');
              }
            }}
            showAllOption={true}
            allOptionLabel={`Все (${statistics?.totalSupplies || 0})`}
            size="medium"
            className="supply-filters"
            style={{ marginBottom: '20px' }}
          />

          {/* Список снабжения */}
          <div className="supply-list">
            {loading ? (
              <div className="supply-loading">
                <i className="ri-loader-4-line ri-spin"></i>
                <span>Загрузка снабжения...</span>
              </div>
            ) : supplyItems.length === 0 ? (
              <div className="supply-empty">
                <i className="ri-truck-line"></i>
                <span>Заявок на снабжение пока нет</span>
                <small>Создайте первую заявку, чтобы начать работу</small>
              </div>
            ) : (
              supplyItems.map((supply) => (
                <div
                  key={supply.id}
                  className={`supply-item ${supply.closed ? 'closed' : ''}`}
                  onClick={() => handleSupplyClick(supply)}
                >
                  <div className="supply-avatar">
                    <i className={supply.closed ? 'ri-check-line' : 'ri-truck-line'}></i>
                  </div>
                  
                  <div className="supply-content">
                    <h3 className="supply-title">{supply.text}</h3>
                    <div className="supply-subtitle">
                      {supply.user?.name || 'Неизвестный'} • {supply.typeOfWorks?.name || 'Без типа работы'}
                    </div>
                    <div className="supply-details">
                      <span className="supply-detail">
                        <i className="ri-time-line"></i>
                        {supply.createdAt ? new Date(supply.createdAt).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Дата неизвестна'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="supply-arrow">
                    <i className="ri-arrow-right-s-line"></i>
                  </div>
                </div>
              ))
            )}
          </div>

      {/* Модальное окно создания заявки на снабжение */}
      <CreateSupplyV2Modal
        isOpen={showCreateModal}
        siteId={siteId}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleSupplyCreated}
      />

          {/* Модальное окно с мессенджером */}
          <QuestionV2MessengerModal
            isOpen={showMessengerModal}
            question={selectedSupply}
            siteId={siteId}
            onClose={() => {
              setShowMessengerModal(false);
              setSelectedSupply(null);
            }}
            onQuestionUpdated={handleSupplyUpdated}
          />
        </>
      )}
    </DashboardCard>
  );
};

export default SupplyCard;